import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { P, H4, H6 } from 'components/Typography';
import _LoadingOverlay from 'components/LoadingOverlay';
import _ButtonUpload from 'components/ButtonUpload';
import _Alert from 'components/Alert';
import { ReadMoreToggle as _ReadMoreToggle } from 'components/Toggle';
import { sizes } from 'style';
import { th } from 'style/theme';

export const Container = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${th('colors.white')};

  @media ${sizes.desktop} {
    flex-direction: row;
    min-height: 768px;
  }
`;

export const LoadingOverlay = styled(_LoadingOverlay)`
  position: fixed;
`;

export const InfoCol = styled.div`
  position: relative;
  max-width: 100%;

  @media ${sizes.desktop} {
    position: sticky;
    top: 0;
    height: 100vh;
    min-height: 768px;
    width: 350px;
  }

  @media ${sizes.largeDesktop} {
    width: 400px;
  }
`;

export const HeadingBg = styled.div`
  @media ${sizes.desktop} {
    position: absolute;
    height: 100%;
    width: 100%;
  }

  > svg {
    position: absolute;
    top: 45px;
    left: 0;
    width: 100%;

    @media ${sizes.desktop} {
      top: 0;
    }
  }
`;

export const InfoContent = styled.div`
  position: relative;
  padding: 70px 20px 10px 20px;

  @media ${sizes.tabletPortrait} {
    padding: 80px 40px 10px 40px;
  }

  @media ${sizes.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: 60px;
    padding-top: 140px;
  }
`;

export const FormHeading = styled(H4)`
  color: ${th('colors.white')};
  margin-bottom: 24px;

  @media ${sizes.desktop} {
    margin-bottom: 60px;
  }
`;

export const ChatLinkDesktop = styled.div`
  display: none;

  @media ${sizes.desktop} {
    display: block;
    margin-top: 40px;
  }
`;

export const Content = styled(motion.div)`
  padding: 20px;
  padding-top: 40px;
  padding-bottom: 40px;

  ${({ isChoosePage }) =>
    !isChoosePage &&
    css`
      padding-top: 60px;
    `}

  @media ${sizes.tabletPortrait} {
    padding: 40px;
    padding-top: 60px;
  }

  @media ${sizes.desktop} {
    align-self: flex-start;
    width: 100%;
    max-width: 500px;
    padding: 0;
    padding: 25vh 0;
    margin-left: 60px;

    ${({ isSummary }) =>
      isSummary &&
      css`
        padding: 15vh 0;
        max-width: 550px;
      `}
  }

  @media ${sizes.largeDesktop} {
    margin-left: 80px;
  }

  @media ${sizes.extraLargeDesktop} {
    max-width: 550px;
    margin-left: 160px;

    ${({ isSummary }) =>
      isSummary &&
      css`
        max-width: 700px;
      `}
  }

  @media ${sizes.monitor} {
    margin-left: 200px;
  }
`;

export const Image = styled.img`
  width: 90%;
  max-width: 450px;
  height: auto;
  pointer-events: none;

  @media ${sizes.mobileMedium} {
    margin-left: 20px;
  }

  @media ${sizes.desktop} {
    position: absolute;
    bottom: 0;
    width: 350px;
    height: auto;
  }

  @media ${sizes.largeDesktop} {
    width: 400px;
  }

  @media ${sizes.extraLargeDesktop} {
    width: 500px;
  }

  ${({ isConfirmation }) =>
    isConfirmation &&
    css`
      margin-bottom: -40px;

      @media ${sizes.desktop} {
        left: 250px;
        max-height: 50%;
        width: auto;
      }
    `}
`;

export const Footer = styled.div`
  position: relative;
  display: flex;

  > img {
    filter: drop-shadow(0px -3px 6px #00000029);
    width: 100%;
  }

  @media ${sizes.tabletPortrait} {
    margin-bottom: -40px;
  }

  @media ${sizes.tabletLandscape} {
    margin-bottom: -60px;
  }

  @media ${sizes.desktop} {
    display: none;
  }
`;

export const ChatLinkMobile = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 20px;

  @media ${sizes.tabletPortrait} {
    top: 30%;
    left: 40px;
    transform: none;
  }
`;

export const IntercomLink = styled.span`
  color: ${th('colors.white')};
  text-decoration: underline;
`;

export const Question = styled.div`
  margin-bottom: 20px;

  @media ${sizes.largeDesktop} {
    margin-bottom: 40px;
  }
`;

export const QuestionHeading = styled.div`
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: bold;

  @media ${sizes.tabletPortrait} {
    margin-bottom: 20px;
  }

  @media ${sizes.largeDesktop} {
    font-size: 20px;
  }
`;

export const Description = styled(P)`
  line-height: 26px;
  margin-bottom: 16px;
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;

  > :last-child {
    margin-left: 8px;
  }
`;

export const Field = styled.div`
  margin-top: 20px;
`;

export const StepHeading = styled(H6)`
  margin-bottom: 20px;
  color: ${th('colors.turquoise')};

  > span {
    font-weight: 400;
  }

  @media ${sizes.desktop} {
    display: none;
  }
`;

export const Instructions = styled(P)`
  margin-top: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 24px;
`;

export const ButtonUpload = styled(_ButtonUpload)`
  margin-top: 26px;
`;

export const File = styled.div`
  margin: 8px 0;
  font-style: italic;
  color: ${({ color }) => th(`colors.${color}`)};
  display: flex;
  align-items: center;

  > span {
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  > svg {
    flex-shrink: 0;
    margin-left: 12px;
    color: ${th('colors.red')};
    cursor: pointer;
  }
`;

export const Alert = styled(_Alert)`
  margin-top: 20px;
`;

export const SummarySection = styled.div`
  position: relative;
  padding-bottom: 22px;
  margin-bottom: 22px;

  ::after {
    content: ' ';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 1px;
    background-color: ${th('colors.turquoise')};
  }
`;

export const SummaryField = styled.div`
  margin-top: 16px;

  @media ${sizes.desktop} {
    display: flex;
    flex-direction: row;
    gap: 40px;

    > :first-child {
      width: 200px;
      flex-shrink: 0;
    }
  }
`;

export const StyledSectionHeading = styled(H6)`
  margin-bottom: 16px;
  color: ${th('colors.turquoise')};

  @media ${sizes.desktop} {
    font-size: 20px;
  }

  > svg {
    position: relative;
    bottom: -3px;
    margin-left: 12px;
    cursor: pointer;
  }
`;

export const SummaryBox = styled.div`
  max-height: 205px;
  padding: 8px;
  margin-top: 8px;
  border: 1px solid ${th('colors.turquoise')};
  border-radius: 5px;
  overflow: scroll;

  @media ${sizes.desktop} {
    width: 100%;
    min-width: 220px;
    margin-top: 0;
  }
`;

export const ReadMoreToggle = styled(_ReadMoreToggle)`
  :not(:last-child) {
    margin-bottom: 20px;
  }
`;
